import React from 'react';
import { Award, Calendar, LogOut, Share2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import ProgressBar from '@/components/ui/ProgressBar';
import { format } from 'date-fns';
import { UserProfile } from '@/services/ProfileService';
import { User } from '@/contexts/authTypes';

interface ProfileHeaderProps {
  user: User | null;
  profile: UserProfile | undefined;
  streak: number;
  wordsLearned: number;
  level: number;
  displayName: string;
  totalXP: number;
  isLoadingXP: boolean;
  handleLogout: () => void;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  streak,
  wordsLearned,
  level,
  displayName,
  totalXP,
  isLoadingXP,
  profile,
  handleLogout
}) => {
  const nextLevelXp = level * 300;
  const xpProgress = Math.min(((totalXP % nextLevelXp) / nextLevelXp) * 100, 100);
  
  const joinDate = profile?.joinedAt ? 
    (profile.joinedAt.toDate ? format(profile.joinedAt.toDate(), 'MMMM yyyy') : 'Recently') : 
    'Recently';
  
  const stats = [
    { label: 'Streak', value: `${streak} days`, icon: <Calendar className="h-4 w-4 text-orange-500" /> },
    { label: 'Words', value: `${wordsLearned}`, icon: <Award className="h-4 w-4 text-primary" /> },
    { label: 'XP', value: isLoadingXP ? "Loading..." : `${totalXP}`, icon: <Star className="h-4 w-4 text-yellow-400" /> },
  ];
  
  return (
    <div className="pt-6">
      <div className="flex flex-col items-center text-center">
        <Avatar className="h-20 w-20 mb-4 ring-4 ring-primary/20">
          <AvatarFallback className="bg-primary text-white text-xl">
            {displayName.substring(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        
        <h1 className="text-2xl font-bold mb-1">{displayName}</h1>
        <p className="text-muted-foreground mb-4">Joined {joinDate}</p>
        
        <div className="grid grid-cols-3 w-full gap-4 mb-4">
          {stats.map((stat, index) => (
            <div key={index} className="flex flex-col items-center">
              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-muted mb-2">
                {stat.icon}
              </div>
              <span className="font-bold">{stat.value}</span>
              <span className="text-xs text-muted-foreground">{stat.label}</span>
            </div>
          ))}
        </div>
        
        <div className="w-full mb-3">
          <div className="flex justify-between text-sm mb-1">
            <span className="font-medium">Level {level}</span>
            <span className="text-muted-foreground">
              {isLoadingXP ? "Loading..." : `${totalXP % nextLevelXp}/${nextLevelXp} XP`}
            </span>
          </div>
          <ProgressBar value={xpProgress} max={100} size="sm" />
        </div>
        
        <div className="flex gap-2 w-full">
          <Button variant="outline" className="flex-1 gap-2">
            <Share2 className="h-4 w-4" />
            Share Profile
          </Button>
          <Button 
            variant="destructive" 
            className="flex-1 gap-2"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" />
            Log Out
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
